import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { create } from 'zustand';
import { useWorkspaceStore } from '@/stores/use-workspace-store';
import { isServerError, listTemplates } from './api';
import type { TemplateSummary } from './catalog';

interface TemplatesState {
  /** Every template the caller can start, in the server's order; null until read. */
  templates: TemplateSummary[] | null;
  refresh: () => Promise<void>;
  reset: () => void;
}

// Each read and each reset takes the next number; only the latest read lands.
let generation = 0;
let inFlight: Promise<void> | null = null;

/** A failed read lists none: offering a template the server may not have ends
 *  in a 404 on Generate, while offering none says plainly that nothing can run. */
export const useTemplatesStore = create<TemplatesState>((set) => ({
  templates: null,
  refresh: () => {
    const mine = ++generation;
    const read = listTemplates()
      .then((templates) => {
        if (mine === generation) set({ templates });
      })
      .catch((error: unknown) => {
        if (isServerError(error)) {
          Sentry.captureException(error);
          console.error('Could not read the templates', error);
        } else {
          console.warn('Could not read the templates', error);
        }
        if (mine === generation) set({ templates: [] });
      })
      .finally(() => {
        if (inFlight === read) inFlight = null;
      });
    inFlight = read;
    return read;
  },
  reset: () => {
    generation += 1;
    inFlight = null;
    set({ templates: null });
  },
}));

function load(force: boolean): void {
  if (inFlight) return;
  if (!force && useTemplatesStore.getState().templates !== null) return;
  void useTemplatesStore.getState().refresh();
}

/** The templates the caller can start, read on first use and again each time
 *  `refreshWhen` turns true; null until the first read answers. */
export function useTemplates(
  { enabled = true, refreshWhen = false }: { enabled?: boolean; refreshWhen?: boolean } = {},
): TemplateSummary[] | null {
  const templates = useTemplatesStore((s) => s.templates);
  const unread = templates === null;

  useEffect(() => {
    if (enabled && unread) load(false);
  }, [enabled, unread]);

  useEffect(() => {
    if (enabled && refreshWhen) load(true);
  }, [enabled, refreshWhen]);

  return enabled ? templates : null;
}

/** The listed template with this id; null while unread or when the caller
 *  cannot list it — a teammate's own template, say. */
export function useTemplate(id: string | null): TemplateSummary | null {
  return useTemplates()?.find((t) => t.id === id) ?? null;
}

// Templates are listed per workspace and per person; after a switch or
// sign-out the list belongs to someone else's context.
const unsubWorkspaceReactivity = useWorkspaceStore.subscribe((state, previous) => {
  if (state.activeWorkspaceId === previous.activeWorkspaceId) return;
  useTemplatesStore.getState().reset();
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => unsubWorkspaceReactivity());
}
